import type { RefObject } from 'preact';
import { useEffect } from 'preact/hooks';

const EMPTY_FN = () => {};
const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * This hook improves keyboard accessibility (a11y) by trapping focus within
 * components like popovers during TAB navigation. It ensures focus cycles
 * internally until the user explicitly exits by pressing ESC or clicking
 * outside, preventing accidental multiple openings or focus leaks to
 * unrelated contexts.
 */
export function useFocusTrap<T extends HTMLElement>(
  previousFocusableRef: RefObject<T>,
  containerRef: RefObject<T>,
  isActive = true
) {
  useEffect(() => {
    const container = containerRef.current;

    if (!isActive || !container) return EMPTY_FN;

    const focusableElements = Array.from(
      container.querySelectorAll(FOCUSABLE_SELECTOR)
    ) as HTMLElement[];

    if (!focusableElements.length) return EMPTY_FN;

    const firstEl = focusableElements.at(0)!;
    const lastEl = focusableElements.at(-1)!;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      // Shift + Tab
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      }
      // Only tab
      else if (document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    firstEl.focus();
    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      previousFocusableRef.current?.focus();
    };
  }, [isActive]);
}
